// client/src/components/plataforma/siteInstitucional/RedesSociaisPage.js
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocialConfigs } from '../../../hooks/useSocialConfigs';
import { Plus, Edit, Trash2, Loader2, GripVertical, Share2, MoreVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import useAlert from '../../../hooks/useAlert'; // Certifique-se de que este caminho está correto
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';

const RedesSociaisPage = () => {
    const navigate = useNavigate();
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const { socialConfigs, setSocialConfigs, platformsOptions, loading, error, deleteSocialConfig, updateSocialConfigsOrder } = useSocialConfigs();
    const { setAlert } = useAlert();

    const [activePlatformFilter, setActivePlatformFilter] = useState('all');
    const [selectedConfig, setSelectedConfig] = useState(null);

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta rede social?')) {
            try {
                await deleteSocialConfig(id); // O hook já exibe o alerta de sucesso/erro
                setSelectedConfig(null);
            } catch (err) {
                // Erro já tratado no hook
            }
        }
    };

    const handleEdit = (config) => {
        setSelectedConfig(null);
        navigate(`/site-institucional/redes-sociais/editar/${config._id}`);
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }

        const items = Array.from(socialConfigs);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);

        const updatedItems = items.map((item, index) => ({ ...item, order: index }));

        setSocialConfigs(updatedItems); // Atualiza o estado local imediatamente para feedback visual
        
        updateSocialConfigsOrder(updatedItems).catch(err => {
            // O erro já é tratado no hook, apenas reverta o estado local
            setSocialConfigs(socialConfigs); // Reverte se houver erro no backend
        });
    };
    
    // Usa as opções de plataformas do hook para o filtro
    const filterOptions = useMemo(() => ['all', ...platformsOptions], [platformsOptions]);

    const filteredConfigs = useMemo(() =>
        (activePlatformFilter === 'all'
            ? socialConfigs
            : socialConfigs.filter(config => config.platform === activePlatformFilter)
        ).sort((a, b) => a.order - b.order), // Garante ordem ao filtrar
        [activePlatformFilter, socialConfigs]
    );

    if (loading) {
        return (
            <div className="page-management-container flex justify-center items-center h-64">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }
    
    // O erro agora é gerenciado pelo useAlert no hook, então este `if (error)` pode ser removido
    // ou adaptado para exibir um componente de erro mais persistente se necessário,
    // mas o `setAlert` já notificará o usuário.

    return (
        <div className="page-management-container">
            <PageHeader
                title="Redes Sociais"
                icon={<Share2 className="icon-title" />}
                navBack={{ to: '/site-institucional', text: 'Voltar' }}
            />

            {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

            <div className="content-panel mt-6">
                <div className="mb-6">
                    <h3 className="text-lg font-medium mb-3">Filtrar por plataforma</h3>
                    <div className="flex flex-wrap gap-2">
                        {filterOptions.map(platform => (
                            <button key={platform} type="button"
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${activePlatformFilter === platform ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                onClick={() => setActivePlatformFilter(platform)}>
                                {platform === 'all' ? 'Todas' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="social-configs-list" isDropDisabled={!hasHomePersonalizada}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredConfigs.map((config, index) => (
                                    <Draggable key={config._id} draggableId={config._id} index={index} isDragDisabled={!hasHomePersonalizada}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col">
                                                <div {...provided.dragHandleProps} className="self-center cursor-grab text-gray-400 hover:text-gray-600 mb-2">
                                                    <GripVertical size={20} />
                                                </div>
                                                <div className="flex-grow">
                                                    <h4 className="font-semibold text-lg capitalize">{config.platform}</h4>
                                                    <p className="text-gray-600 text-sm mb-1">@{config.username}</p>
                                                    <a href={config.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-sm break-all hover:underline">
                                                        {config.url}
                                                    </a>
                                                </div>
                                                <div className="flex justify-between items-center border-t pt-3 mt-auto">
                                                    <button onClick={() => setSelectedConfig(config)} className="action-icon-button text-gray-600" title="Ações" disabled={!hasHomePersonalizada}><MoreVertical size={18} /></button>
                                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${config.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {config.active ? 'Ativo' : 'Inativo'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {filteredConfigs.length === 0 && !loading && <div className="col-span-full text-center py-12 text-gray-500">Nenhuma rede social configurada.</div>}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            <ActionsDrawer
                open={!!selectedConfig}
                onClose={() => setSelectedConfig(null)}
                title={selectedConfig ? `@${selectedConfig.username}` : 'Rede Social'}
                subtitle={selectedConfig?.platform}
                actions={selectedConfig ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEdit(selectedConfig),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedConfig._id),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={() => navigate('/site-institucional/redes-sociais/nova')} title="Nova Rede Social" icon={<Share2 size={26} className="icon" />} disabled={!hasHomePersonalizada} />
        </div>
    );
};

export default RedesSociaisPage;